/* eslint-disable no-underscore-dangle */
import { useCallback, useMemo } from 'react';
import { useInstantSearch, useRefinementList } from 'react-instantsearch';
import styled, { css } from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { LeagueAvatar } from '@sorare/core/src/components/league/LeagueAvatar';
import { FilterSection } from '@sorare/core/src/components/search/FilterSection';
import { FilterTitle } from '@sorare/core/src/components/search/FilterTitle';
import type { ExtendedUIState } from '@sorare/core/src/components/search/InstantSearch/types';
import { Option } from '@sorare/core/src/components/search/Option';
import type { ConfigQuery } from '@sorare/core/src/contexts/config/types';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useSearchCardsContext } from '@sorare/core/src/contexts/searchCards';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useSo5Competitions } from '@sorare/core/src/hooks/useSo5Competitions';
import { useVirtualToggle } from '@sorare/core/src/hooks/useVirtualToggle';
import { useVirtualToggleManager } from '@sorare/core/src/hooks/useVirtualToggleManager';
import { joinFiltersWithAnd } from '@sorare/core/src/lib/algolia';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import type { FilterWidget } from '@sorare/core/src/lib/filters';
import { FILTERS, VIRTUAL_TOGGLE_FILTERS } from '@sorare/core/src/lib/filters';

import { useFacetCounts } from 'hooks/search/useFacetCounts';

const FILTER = VIRTUAL_TOGGLE_FILTERS.competitionEligibilityFilter;

const ActiveLeagues = styled.div`
  padding-left: var(--double-unit);
`;

const divider = css`
  position: relative;
  isolation: isolate;
  padding-top: var(--double-unit);
  &:before {
    content: '';
    position: absolute;
    top: var(--unit);
    left: 0;
    width: 100%;
    height: 1px;
    background-color: var(--c-nd-100);
  }
`;
const OptionWrapper = styled.div`
  &:has(.active-leagues) {
    ${divider}
  }
  &:has(.active-leagues) + .option-wrapper:not(:has(.active-leagues)) {
    ${divider}
  }
`;

const useCurrentLeaguesRefinements = ({
  sport,
  competition,
  skip,
}: {
  sport: Sport;
  competition: string | undefined;
  skip: boolean;
}) => {
  const { results } = useInstantSearch();
  const { language } = useIntlContext();
  const { filterableSo5Competitions } = useSo5Competitions();

  const { filters } = useSearchCardsContext();
  const i18nLeagueAttribute = FILTERS.activeLeague.i18nAttribute(language);

  const filtersWithoutSearchCardsContextFilters =
    filters.length > 0
      ? results._state.filters?.replace(
          ` AND ${joinFiltersWithAnd(filters)}`,
          ''
        )
      : results._state.filters;

  const params = useMemo(
    () => ({
      ...results._state,
      maxValuesPerFacet: 100,
      filters: filtersWithoutSearchCardsContextFilters,
      disjunctiveFacetsRefinements: {
        ...results._state.disjunctiveFacetsRefinements,
        [i18nLeagueAttribute]: [],
      },
      facetFilters: [
        ...(results._state.facetFilters || []),
        filterableSo5Competitions.flatMap(({ competitions }) =>
          competitions.map(({ slug }) => `active_competitions:${slug}`)
        ),
      ],
    }),
    // force lifecycle to run when competition or results change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [results._state, competition]
  );

  const leagues = useFacetCounts({
    index: results.index,
    attribute: i18nLeagueAttribute,
    skip,
    params,
    sport,
  });

  const { refine: refineLeague, items: refinedLeagues } = useRefinementList({
    attribute: i18nLeagueAttribute,
    limit: 100,
  });

  const clearRefinedLeagues = useCallback(() => {
    refinedLeagues.forEach(
      ({ value, isRefined }) => isRefined && refineLeague(value)
    );
  }, [refineLeague, refinedLeagues]);

  return {
    leagues,
    refineLeague,
    refinedLeagues: refinedLeagues
      .filter(({ isRefined }) => isRefined)
      .map(({ value }) => value),
    clearRefinedLeagues,
  };
};

export const SimpleCompetitionEligibilityFilter = ({
  currentRefinement,
  onChange,
  clearRefinedLeagues,
  leagues,
  refinedLeagues,
  refineLeague,
  filterableSo5Competitions,
}: {
  currentRefinement: string | undefined;
  onChange: (so5CompetitionSlug: string) => void;
  clearRefinedLeagues: () => void;
  leagues: Record<string, number | undefined>;
  refinedLeagues: string[];
  refineLeague: (league: string) => void;
  filterableSo5Competitions: ConfigQuery['so5']['so5Competitions'];
}) => {
  const refineAllLeaguesFromCompetitionExcept = useCallback(
    (competitions: string[], league: string) => {
      const leaguesToRefine = Object.keys(leagues).filter(
        l => competitions.includes(l.split('|')[1]) && l !== league
      );
      leaguesToRefine.forEach(l => refineLeague(l));
    },
    [leagues, refineLeague]
  );

  return (
    <FilterSection>
      {filterableSo5Competitions.map(so5Competition => (
        <OptionWrapper className="option-wrapper" key={so5Competition.slug}>
          <Option
            active={currentRefinement === so5Competition.slug}
            onClick={() => {
              onChange(so5Competition.slug);
              clearRefinedLeagues();
            }}
            variant="radio"
          >
            {so5Competition.displayName}
          </Option>
          {so5Competition.competitions.length > 1 &&
            Object.entries(leagues).length > 1 && (
              <ActiveLeagues className="active-leagues">
                {Object.entries(leagues).map(([league, count]) => {
                  if (
                    so5Competition.competitions
                      .map(s => s.slug)
                      .some(s => league.endsWith(`|${s}`))
                  ) {
                    const parentCompetitionIsRefined =
                      currentRefinement === so5Competition.slug;
                    const leagueRefined = refinedLeagues.includes(league);
                    const [label, slug] = league.split('|');
                    return (
                      <Option
                        key={slug}
                        count={count}
                        active={parentCompetitionIsRefined || leagueRefined}
                        onClick={() => {
                          if (parentCompetitionIsRefined) {
                            // this means all nested leagues are refined
                            // toggle off the parent competition
                            onChange(so5Competition.slug);
                            // toggle on all other leagues, while this one will be off
                            refineAllLeaguesFromCompetitionExcept(
                              so5Competition.competitions.map(s => s.slug),
                              league
                            );
                          } else {
                            // toggle off the competititon refinement if it's on
                            if (currentRefinement) onChange(currentRefinement);
                            if (!leagueRefined && so5Competition) {
                              // this means the league will get refined so if all siblings are also refined,
                              // toggle the competition
                              const allSiblingsSlugs =
                                so5Competition.competitions
                                  .map(s => s.slug)
                                  .filter(s => s !== slug);
                              const siblingsRefined = allSiblingsSlugs
                                .map(s =>
                                  refinedLeagues.find(l => l.endsWith(`|${s}`))
                                )
                                .filter(Boolean);
                              if (
                                siblingsRefined.length ===
                                allSiblingsSlugs.length
                              ) {
                                // toggle on parent competition
                                onChange(so5Competition.slug);
                                // toggle off all leagues (they will still appear on)
                                siblingsRefined.forEach(l => refineLeague(l));
                                return;
                              }
                            }
                            // toggle the league
                            refineLeague(league);
                          }
                        }}
                        variant="checkbox"
                      >
                        <LeagueAvatar
                          size={24}
                          league={{
                            name: label,
                            slug,
                          }}
                        />
                        <span>{label}</span>
                      </Option>
                    );
                  }
                  return null;
                })}
              </ActiveLeagues>
            )}
        </OptionWrapper>
      ))}
    </FilterSection>
  );
};

const CompetitionEligibilityFilter = ({ visible }: { visible: boolean }) => {
  const { sport } = useSportContext();
  const setVirtualToggle = useVirtualToggleManager();
  const { filterableSo5Competitions } = useSo5Competitions();
  const { currentRefinement } = useVirtualToggle<string>({
    name: FILTER.name,
  });
  const track = useEvents();

  const { refineLeague, refinedLeagues, leagues, clearRefinedLeagues } =
    useCurrentLeaguesRefinements({
      sport,
      competition: currentRefinement,
      skip: !visible || sport !== Sport.FOOTBALL,
    });

  const onChange = useCallback(
    (so5CompetitionSlug: string) => {
      const isActive = currentRefinement === so5CompetitionSlug;
      const newValue = isActive ? undefined : so5CompetitionSlug;

      setVirtualToggle({
        [FILTER.name]: newValue,
      });

      if (!isActive) {
        track('Use Market Filter', {
          filterName: FILTER.trackingName,
          filterValue: so5CompetitionSlug,
        });
      }
    },
    [currentRefinement, setVirtualToggle, track]
  );

  if (
    filterableSo5Competitions.some(s => s.displayName === currentRefinement)
  ) {
    const backwardCompatibleSlug = filterableSo5Competitions.find(
      s => s.displayName === currentRefinement
    )?.slug;
    setVirtualToggle({
      [FILTER.name]: backwardCompatibleSlug,
    });
  }

  if (!visible) return <FilterSection />;

  return (
    <SimpleCompetitionEligibilityFilter
      currentRefinement={currentRefinement}
      onChange={onChange}
      clearRefinedLeagues={clearRefinedLeagues}
      leagues={leagues}
      refinedLeagues={refinedLeagues}
      refineLeague={refineLeague}
      filterableSo5Competitions={filterableSo5Competitions}
    />
  );
};

const Title = () => {
  const { indexUiState } = useInstantSearch<ExtendedUIState>();
  const selected = !!indexUiState?.virtualToggle?.[FILTER.name];

  return <FilterTitle name={FILTER.title!} selected={selected} />;
};

export const widget: FilterWidget = {
  key: FILTER.name,
  type: 'virtualToggle',
  filter: FILTER,
  Component: CompetitionEligibilityFilter,
  title: <Title />,
};
